import { cn } from '@cannect/lib/utils'
import { Link } from '@cannect/new-components/atoms'
import type { TLinkItem } from '@cannect/new-components/molecules/LinkItem/types'
import { type VariantProps, tv } from 'tailwind-variants'

/**
 * Pill de navegação do header do Novo Marketplace (Figma node 4161:23973).
 *
 * O protótipo monta esses três elementos como frames soltos (`Frame 3465864`, `Frame 3466702`,
 * `Frame 3466207`) sem componente nem estados definidos. Aqui eles viram um componente único
 * com variantes, e os estados que faltavam no design (hover, focus visível, ativo) são supridos.
 *
 * Medidas do design: altura 25px, padding lateral 12px, raio 30px, texto 12px/1.4.
 */
const headerPill = tv({
  base: [
    'inline-flex h-[25px] shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-full',
    // `font-normal` explícito: o átomo Link aplica `font-medium` por padrão e o design
    // pede Poppins Regular (400) nos itens do header — só "Minha conta" é SemiBold.
    'border border-solid px-3 font-sans text-xs font-normal leading-[1.4] no-underline transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-marketplace-800 focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-60'
  ],
  variants: {
    variant: {
      /** Item de navegação: sem borda, fundo só no hover. */
      nav: 'border-transparent bg-transparent text-marketplace-800 hover:bg-marketplace-200',
      /** Ação secundária: contorno neutro. */
      outline:
        'border-neutral-100 bg-transparent text-marketplace-800 hover:border-marketplace-300 hover:bg-marketplace-100',
      /** Ação primária: preenchido. */
      solid: 'border-marketplace-900 bg-marketplace-800 text-neutral-50 hover:bg-marketplace-900'
    },
    isActive: {
      true: ''
    }
  },
  compoundVariants: [
    { variant: 'nav', isActive: true, class: 'bg-marketplace-200 font-semibold' },
    { variant: 'outline', isActive: true, class: 'border-marketplace-300 bg-marketplace-100' }
  ],
  defaultVariants: { variant: 'nav', isActive: false }
})

export type THeaderPillProps = Pick<TLinkItem, 'title' | 'path' | 'external' | 'onClick'> &
  VariantProps<typeof headerPill> & {
    className?: string
  }

export const HeaderPill = ({ title, path, external, onClick, variant, isActive, className }: THeaderPillProps) => {
  const classes = cn(headerPill({ variant, isActive }), className)

  // Sem rota: age como botão (ex.: acessos que disparam SSO/modal).
  if (!path || path === '#') {
    return (
      <button type="button" onClick={onClick} className={cn(classes, 'cursor-pointer appearance-none')}>
        {title}
      </button>
    )
  }

  return (
    <Link
      to={path}
      isExternal={external}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      onClick={onClick}
      className={classes}>
      {title}
    </Link>
  )
}
